/**
 * Which options the current selection rules out (R28): an option may name
 * other fields' codes it `requires`, and the item page greys out one whose
 * requirement the selection does not meet.
 *
 * The backend refuses such a combination anyway (a `requires_option` field
 * error); this only stops the orderer picking it. Mirrors the backend's reading
 * of "what is selected": a field with no selection counts as its default
 * option, and a value matching no preset (a custom value) meets no requirement.
 */

import type { CatalogueField, CatalogueOption } from "../data/catalogue-types";

function selectedOption(
  field: CatalogueField,
  values: Record<string, string>,
): CatalogueOption | undefined {
  const options = field.options ?? [];
  const code = values[field.key];
  if (code === undefined) return options.find((o) => o.isDefault);
  return options.find((o) => o.code === code);
}

/** Each field's option codes the current selection rules out, keyed by field key. */
export function unavailableOptionCodes(
  fields: readonly CatalogueField[],
  values: Record<string, string>,
): Map<string, Set<string>> {
  const selected = new Map(
    fields.map((f) => [f.key, selectedOption(f, values)] as const),
  );
  const unavailable = new Map<string, Set<string>>();
  for (const field of fields) {
    for (const option of field.options ?? []) {
      const unmet = Object.entries(option.requires ?? {}).some(
        ([needed, codes]) => {
          const chosen = selected.get(needed);
          return chosen === undefined || !codes.includes(chosen.code);
        },
      );
      if (unmet) {
        const codes = unavailable.get(field.key) ?? new Set<string>();
        codes.add(option.code);
        unavailable.set(field.key, codes);
      }
    }
  }
  return unavailable;
}

/** "Needs Pages: 8pp or 16pp" — why an option is greyed out, in the orderer's words. */
export function optionRequirementHint(
  option: CatalogueOption,
  fields: readonly CatalogueField[],
): string | undefined {
  const parts = Object.entries(option.requires ?? {}).map(([needed, codes]) => {
    const field = fields.find((f) => f.key === needed);
    const labels = codes.map(
      (code) => field?.options?.find((o) => o.code === code)?.label ?? code,
    );
    return `${field?.label ?? needed}: ${labels.join(" or ")}`;
  });
  return parts.length > 0 ? `Needs ${parts.join("; ")}` : undefined;
}
